import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { differenceInDays } from 'date-fns';
import { Subscription } from 'rxjs';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { VideoResponseDto } from '@/app/common/general-types/videos.dto';
import { VideoFormatEnum } from '@/app/common/general-types/briefs.dto';
import { REVIEW_NOTE_MAX_LENGTH, normalizeReviewNote } from '@/app/common/general-types/recruit-groups.review';
import { FormatService } from '@/app/common/format/format.service';
import { MiscService } from '@/app/services/misc/misc.service';
import { ImageService } from '@/app/services/image/image.service';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { MagicSearchService } from '@/app/pages/creators/service/magic-search/magic-search.service';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';

export type ShareReviewFeedback = {
  decision: 'approve' | 'decline' | 'feedback';
  note: string;
};

@Component({
  selector: 'app-share-review-creator',
  imports: [DecimalPipe, LdgImgComponent, VideoThumbnailComponent],
  templateUrl: './share-review-creator.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareReviewCreator implements AfterViewInit, OnDestroy {
  readonly creator = input.required<CreatorResponseDto>();
  readonly shareToken = input('');
  readonly approved = input(false);
  readonly declined = input(false);
  readonly invited = input(false);
  readonly inviteOnApprove = input(false);
  readonly reviewLabel = input<string | undefined>(undefined);
  readonly reviewNote = input<string | undefined>(undefined);

  readonly approve = output<void>();
  readonly decline = output<void>();
  readonly feedback = output<ShareReviewFeedback>();

  readonly videoFormat = VideoFormatEnum.Vertical;
  readonly noteMaxLength = REVIEW_NOTE_MAX_LENGTH;
  readonly portfolioPlaceholders = [0, 1, 2] as const;

  private readonly imageService = inject(ImageService);
  private readonly formatService = inject(FormatService);
  private readonly miscService = inject(MiscService);
  private readonly recruitGroupService = inject(RecruitGroupService);
  private readonly magicSearchService = inject(MagicSearchService);

  private readonly subscriptionList = new Subscription();
  private intersectionObserver?: IntersectionObserver;
  private portfolioLoadStarted = false;

  readonly cardRoot = viewChild<ElementRef<HTMLElement>>('cardRoot');

  readonly feedbackOpen = signal(false);
  readonly draftFeedback = signal('');
  readonly isInView = signal(false);
  readonly portfolioLoading = signal(false);
  readonly portfolioVideos = signal<VideoResponseDto[]>([]);
  readonly portfolioPreviewVideos = signal<VideoResponseDto[]>([]);

  readonly hasDraftFeedback = computed(() => !!normalizeReviewNote(this.draftFeedback()));

  readonly feedbackOnly = computed(() =>
    !!this.reviewNote() && !this.approved() && !this.declined(),
  );

  readonly approveSelected = computed(() => this.approved() && !this.declined());

  readonly reviewLabelTextClass = computed(() => {
    if (this.declined()) {
      return 'text-rose-700 dark:text-rose-300';
    }
    if (this.approved()) {
      return 'text-green-700 dark:text-green-300';
    }
    if (this.feedbackOnly()) {
      return 'text-sky-700 dark:text-sky-300';
    }
    return 'text-primary';
  });

  readonly displayName = computed(() => {
    const creator = this.creator();
    const lastInitial = creator.lastName?.charAt(0);
    return lastInitial ? `${creator.firstName} ${lastInitial}.` : (creator.firstName ?? 'Creator');
  });

  readonly hometown = computed(() => this.miscService.getShortHometown(this.creator().homeTownName));

  readonly creatorAge = computed(() => this.formatService.calculateAge(this.creator().dateOfBirth));

  readonly photoSrc = computed(() =>
    this.imageService.getImageSource(this.creator().photoURL, 'gallery_lg'),
  );

  readonly noteFieldId = computed(() => `share-review-note-${this.creator().blazeFirebaseUid}`);

  readonly createdDaysAgo = computed(() =>
    differenceInDays(new Date(), this.creator().createdDate),
  );

  readonly isUnrated = computed(() => {
    const rating = this.creator().reviews_summary?.average_rating ?? this.creator().rating;
    return this.createdDaysAgo() < 7 && !rating;
  });

  readonly ratingValue = computed(() =>
    this.creator().reviews_summary?.average_rating ?? this.creator().rating,
  );

  readonly totalFollowersLabel = computed(() => {
    const creator = this.creator();
    return this.formatService.convertIntToString(
      (creator.tikTokFollowers ?? 0)
      + (creator.instaFollowers ?? 0)
      + (creator.snapchatFollowers ?? 0)
      + (creator.youtubeSubscribers ?? 0),
    );
  });

  readonly avgViewsLabel = computed(() =>
    this.formatService.convertIntToString(this.averageViews()),
  );

  readonly avgEngagementLabel = computed(() => this.averageEngagement().toFixed(2));

  readonly hasAvgStats = computed(() => this.averageViews() > 0 || this.averageEngagement() > 0);

  readonly displayBio = computed(() => {
    const bio = this.creator().bio?.trim();
    return bio && bio.length > 0 ? bio : 'Bio coming soon...';
  });

  readonly hasRealBio = computed(() => (this.creator().bio?.trim().length ?? 0) > 0);

  readonly interestLabels = computed(() =>
    (this.creator().interests ?? [])
      .slice(0, 3)
      .map((interest) => this.formatService.getInterestText(String(interest)))
      .filter((label) => label.length > 0 && label !== 'Loading...'),
  );

  readonly tiktokFollowersLabel = computed(() => {
    const count = this.creator().tikTokFollowers ?? 0;
    return count > 0 ? this.formatService.convertIntToString(count) : '—';
  });

  readonly instaFollowersLabel = computed(() => {
    const count = this.creator().instaFollowers ?? 0;
    return count > 0 ? this.formatService.convertIntToString(count) : '—';
  });

  readonly snapFollowersLabel = computed(() => {
    const count = this.creator().snapchatFollowers ?? 0;
    return count > 0 ? this.formatService.convertIntToString(count) : '—';
  });

  readonly youtubeFollowersLabel = computed(() => {
    const count = this.creator().youtubeSubscribers ?? 0;
    return count > 0 ? this.formatService.convertIntToString(count) : '—';
  });

  ngAfterViewInit(): void {
    const element = this.cardRoot()?.nativeElement;
    if (!element || typeof IntersectionObserver === 'undefined') {
      this.isInView.set(true);
      this.loadPortfolioVideos();
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }
        this.isInView.set(true);
        this.loadPortfolioVideos();
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = undefined;
      },
      { rootMargin: '240px 0px' },
    );
    this.intersectionObserver.observe(element);
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.subscriptionList.unsubscribe();
  }

  launchPortfolio(): void {
    this.magicSearchService.portfolioCreatorId.set(this.creator().blazeFirebaseUid);
    this.magicSearchService.portfolioCreator.set(this.creator());
  }

  previewVideo(video: VideoResponseDto): void {
    const playlist = this.portfolioVideos();
    this.magicSearchService.previewVideoPlaylist.set(playlist.length > 0 ? playlist : [video]);
    this.magicSearchService.previewVideo.set(video);
  }

  hasPortfolioThumbnail(video: VideoResponseDto): boolean {
    return !!video.thumbnailURL && !video.thumbnailURL.includes('www.tikwm.com');
  }

  toggleFeedback(): void {
    const opening = !this.feedbackOpen();
    this.feedbackOpen.set(opening);
    if (opening) {
      this.draftFeedback.set(this.reviewNote() ?? '');
    }
  }

  onDraftFeedbackInput(event: Event): void {
    this.draftFeedback.set((event.target as HTMLTextAreaElement).value);
  }

  submitFeedback(decision: ShareReviewFeedback['decision']): void {
    this.feedback.emit({
      decision,
      note: normalizeReviewNote(this.draftFeedback()),
    });
    this.feedbackOpen.set(false);
  }

  submitFeedbackApprove(): void {
    this.submitFeedback('approve');
  }

  submitFeedbackDecline(): void {
    this.submitFeedback('decline');
  }

  submitFeedbackOnly(): void {
    this.submitFeedback('feedback');
  }

  private loadPortfolioVideos(): void {
    if (this.portfolioLoadStarted) {
      return;
    }
    const token = this.shareToken().trim();
    const creatorId = this.creator().blazeFirebaseUid;
    if (!token || !creatorId) {
      return;
    }
    this.portfolioLoadStarted = true;
    this.portfolioLoading.set(true);
    this.subscriptionList.add(
      this.recruitGroupService.getSharedCreatorVideos(token, creatorId).subscribe({
        next: (videos) => {
          this.portfolioVideos.set(videos);
          this.portfolioPreviewVideos.set(videos.slice(0, 3));
          this.portfolioLoading.set(false);
        },
        error: () => {
          // Allow a retry: without this a transient failure leaves the card
          // permanently showing the empty state.
          this.portfolioLoadStarted = false;
          this.portfolioLoading.set(false);
        },
      }),
    );
  }

  private averageViews(): number {
    const ssCreator = this.creator().ssCreator;
    if (!ssCreator) {
      return 0;
    }
    const instagram = ssCreator.instagramAverages?.averageViews ?? 0;
    const tiktok = ssCreator.tiktokAverages?.averageViews ?? 0;
    if (ssCreator.instagramAverages && ssCreator.tiktokAverages) {
      return (instagram + tiktok) / 2;
    }
    return instagram || tiktok;
  }

  private averageEngagement(): number {
    const ssCreator = this.creator().ssCreator;
    if (!ssCreator) {
      return 0;
    }
    const instagram = ssCreator.instagramAverages?.averageEngagementRate ?? 0;
    const tiktok = ssCreator.tiktokAverages?.averageEngagementRate ?? 0;
    if (ssCreator.instagramAverages && ssCreator.tiktokAverages) {
      return (instagram + tiktok) / 2;
    }
    return instagram || tiktok;
  }
}
